// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import {
	formatGamePlayDuration,
	getLafonGameImageUrl,
	getSteamIconUrlCandidates,
	isSteamAppId,
	sanitizeGameIconDataUrl,
} from '@app/features/game_activity/GameActivity';
import GameActivityStore from '@app/features/game_activity/GameActivityStore';
import {useGameActivityPresence} from '@app/features/game_activity/hooks/useGameActivityPresence';
import {http} from '@app/features/platform/transport/RestTransport';
import {VoiceActivitySection} from '@app/features/user/components/profile/VoiceActivitySection';
import styles from '@app/features/user/components/profile/ProfileActivityPanel.module.css';
import type {
	GameHistoryEntryResponse,
	GameHistoryResponse,
} from '@fluxer/schema/src/domains/user/GameHistorySchemas';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {GameControllerIcon, SparkleIcon, TimerIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useMemo, useState} from 'react';

const CURRENT_ACTIVITY = msg({
	message: 'Current activity',
	context: 'profile-activity',
	comment: 'Heading above the game the user is playing right now, in the profile activity tab.',
});
const RECENT_ACTIVITY = msg({
	message: 'Recent activity',
	context: 'profile-activity',
	comment: 'Heading above the list of games played recently, in the profile activity tab.',
});
const RECENT_ACTIVITY_HINT = msg({
	message: 'Games played in the last 30 days.',
	context: 'profile-activity',
	comment: 'Explanation under the recent activity heading on a profile.',
});
const PLAYING = msg({
	message: 'Playing',
	comment: 'Section title on a user profile when the user is playing a Steam game.',
});
const MARATHON = msg({
	message: '{hours}-hour marathon',
	context: 'profile-activity',
	comment: 'Badge on a recently played game: longest single session in hours. Preserve {hours}.',
});
const NEW_PLAYER = msg({
	message: 'New player',
	context: 'profile-activity',
	comment: 'Badge on a recently played game the user only started playing recently.',
});

const HISTORY_CACHE_TTL_MS = 60_000;
const MARATHON_MIN_MS = 2 * 60 * 60 * 1000;
const NEW_PLAYER_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
const historyCache = new Map<string, {at: number; games: Array<GameHistoryEntryResponse>}>();

function useGameHistory(userId: string): Array<GameHistoryEntryResponse> | null {
	const cached = historyCache.get(userId);
	const [games, setGames] = useState<Array<GameHistoryEntryResponse> | null>(cached?.games ?? null);
	useEffect(() => {
		const entry = historyCache.get(userId);
		if (entry && Date.now() - entry.at < HISTORY_CACHE_TTL_MS) {
			setGames(entry.games);
			return;
		}
		let cancelled = false;
		http
			.get<GameHistoryResponse>(Endpoints.USER_GAME_HISTORY(userId))
			.then((response) => {
				const next = response.body.games ?? [];
				historyCache.set(userId, {at: Date.now(), games: next});
				if (!cancelled) setGames(next);
			})
			.catch(() => {
				if (!cancelled) setGames([]);
			});
		return () => {
			cancelled = true;
		};
	}, [userId]);
	return games;
}

// Newer Steam apps only publish their header under a hashed path; the legacy path serves a grey
// placeholder for them. Known ones are listed here, the rest are caught by `isSteamPlaceholder`.
const KNOWN_STEAM_HEADERS: Readonly<Record<string, string>> = {
	'2807960':
		'https://shared.akamai.steamstatic.com/store_item_assets/steam/apps/2807960/c12d12ce3c7d217398d3fcad77427bfc9d57c570/header.jpg',
	'3028330':
		'https://shared.akamai.steamstatic.com/store_item_assets/steam/apps/3028330/7f156ef258b0b34011363f8da61b7e2a714232c7/header.jpg',
};

function steamHeaderUrl(steamAppId: string): string | null {
	if (!isSteamAppId(steamAppId) || getLafonGameImageUrl(steamAppId)) return null;
	const known = KNOWN_STEAM_HEADERS[steamAppId];
	if (known) return known;
	return `https://shared.fastly.steamstatic.com/store_item_assets/steam/apps/${encodeURIComponent(steamAppId)}/header.jpg`;
}

/** Steam's placeholder is a flat grey image; a real header always has colour or contrast. */
function isSteamPlaceholder(img: HTMLImageElement): boolean {
	try {
		const canvas = document.createElement('canvas');
		canvas.width = 8;
		canvas.height = 4;
		const g = canvas.getContext('2d', {willReadFrequently: true});
		if (!g) return false;
		g.drawImage(img, 0, 0, 8, 4);
		const d = g.getImageData(0, 0, 8, 4).data;
		let min = 255;
		let max = 0;
		for (let i = 0; i < d.length; i += 4) {
			if (Math.abs(d[i] - d[i + 1]) > 6 || Math.abs(d[i + 1] - d[i + 2]) > 6) return false;
			min = Math.min(min, d[i]);
			max = Math.max(max, d[i]);
		}
		return max - min < 24;
	} catch {
		return false;
	}
}

const GameArt: React.FC<{
	steamAppId: string;
	steamIconHash?: string | null;
	iconDataUrl?: string | null;
	large?: boolean;
}> = ({steamAppId, steamIconHash, iconDataUrl, large}) => {
	const urls = useMemo(() => {
		const list: Array<{url: string; cover: boolean}> = [];
		const header = steamHeaderUrl(steamAppId);
		if (header) list.push({url: header, cover: true});
		const local = sanitizeGameIconDataUrl(iconDataUrl);
		if (local) list.push({url: local, cover: false});
		const lafonImage = getLafonGameImageUrl(steamAppId);
		if (lafonImage) list.push({url: lafonImage, cover: true});
		for (const url of getSteamIconUrlCandidates(steamAppId, steamIconHash)) {
			if (url !== lafonImage) list.push({url, cover: false});
		}
		return list;
	}, [steamAppId, steamIconHash, iconDataUrl]);
	const [failed, setFailed] = useState(0);
	useEffect(() => setFailed(0), [urls]);
	const current = urls[failed];
	const isSteamCover = Boolean(current?.cover && current.url.includes('.steamstatic.com/'));
	return (
		<div className={clsx(styles.art, large && styles.artLarge)} aria-hidden onContextMenu={(e) => e.preventDefault()}>
			{current ? (
				<img
					key={current.url}
					src={current.url}
					alt=""
					draggable={false}
					referrerPolicy="no-referrer"
					// Steam's CDN allows CORS, which lets us read pixels to spot its grey placeholder.
					crossOrigin={isSteamCover ? 'anonymous' : undefined}
					className={current.cover ? styles.artImage : styles.artIcon}
					onLoad={(event) => {
						if (isSteamCover && isSteamPlaceholder(event.currentTarget)) setFailed((count) => count + 1);
					}}
					onError={() => setFailed((count) => count + 1)}
				/>
			) : (
				<GameControllerIcon weight="fill" className={styles.artIcon} />
			)}
		</div>
	);
};

function formatRelative(iso: string, locale: string): string {
	const diffMs = new Date(iso).getTime() - Date.now();
	const rtf = new Intl.RelativeTimeFormat(locale, {numeric: 'auto'});
	const abs = Math.abs(diffMs);
	const minute = 60_000;
	const hour = 60 * minute;
	const day = 24 * hour;
	if (abs < hour) return rtf.format(Math.round(diffMs / minute), 'minute');
	if (abs < day) return rtf.format(Math.round(diffMs / hour), 'hour');
	if (abs < 7 * day) return rtf.format(Math.round(diffMs / day), 'day');
	return rtf.format(Math.round(diffMs / (7 * day)), 'week');
}

const CurrentGameCard: React.FC<{userId: string}> = observer(({userId}) => {
	const {i18n} = useLingui();
	const remote = useGameActivityPresence(userId);
	const activity = userId === Authentication.currentUserId ? GameActivityStore.getPresenceActivity() : remote;
	const [now, setNow] = useState(() => Date.now());
	useEffect(() => {
		if (!activity?.startedAtMs) return;
		const timer = window.setInterval(() => setNow(Date.now()), 1000);
		return () => window.clearInterval(timer);
	}, [activity?.startedAtMs]);
	if (!activity?.name) return null;
	return (
		<div className={styles.card}>
			<div className={styles.cardLabel}>{i18n._(PLAYING)}</div>
			<div className={styles.cardRow}>
				<GameArt
					steamAppId={activity.steamAppId}
					steamIconHash={activity.steamIconHash}
					iconDataUrl={activity.iconDataUrl}
					large
				/>
				<div className={styles.body}>
					<div className={styles.gameName}>{activity.name}</div>
					{activity.startedAtMs ? (
						<div className={clsx(styles.meta, styles.timer)}>
							<span className={styles.metaItem}>
								<GameControllerIcon weight="fill" className={styles.metaIcon} />
								{formatGamePlayDuration(now - activity.startedAtMs)}
							</span>
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
});

interface ProfileActivityPanelProps {
	userId: string;
	onNavigate?: () => void;
}

export const ProfileActivityPanel: React.FC<ProfileActivityPanelProps> = observer(({userId, onNavigate}) => {
	const {i18n} = useLingui();
	const remote = useGameActivityPresence(userId);
	const currentActivity = userId === Authentication.currentUserId ? GameActivityStore.getPresenceActivity() : remote;
	const history = useGameHistory(userId);
	const recent = (history ?? []).filter((game) => game.steam_app_id !== currentActivity?.steamAppId);
	const nowMs = Date.now();
	return (
		<div className={styles.panel} data-flx="user.profile-activity-panel">
			<div className={clsx(styles.section, styles.currentSection)}>
				{currentActivity?.name ? <div className={styles.sectionTitle}>{i18n._(CURRENT_ACTIVITY)}</div> : null}
				<CurrentGameCard userId={userId} />
				<VoiceActivitySection userId={userId} onNavigate={onNavigate} showAllActivities={true} />
			</div>
			{recent.length > 0 ? (
				<div className={styles.section}>
					<div className={styles.sectionTitle}>{i18n._(RECENT_ACTIVITY)}</div>
					<div className={styles.sectionHint}>{i18n._(RECENT_ACTIVITY_HINT)}</div>
					{recent.map((game) => {
						const marathonHours = Math.floor(game.longest_session_ms / (60 * 60 * 1000));
						const isNewPlayer = nowMs - new Date(game.first_played_at).getTime() < NEW_PLAYER_WINDOW_MS;
						return (
							<div key={game.steam_app_id} className={styles.card}>
								<div className={styles.cardRow}>
									<GameArt steamAppId={game.steam_app_id} steamIconHash={game.steam_icon_hash} />
									<div className={styles.body}>
										<div className={styles.gameName}>{game.name}</div>
										<div className={styles.meta}>
											<span className={styles.metaItem}>
												<GameControllerIcon weight="fill" className={styles.metaIcon} />
												{formatRelative(game.last_played_at, i18n.locale)}
											</span>
											{game.longest_session_ms >= MARATHON_MIN_MS ? (
												<span className={styles.metaItem}>
													<TimerIcon weight="fill" className={styles.metaIcon} />
													{i18n._(MARATHON, {hours: marathonHours})}
												</span>
											) : isNewPlayer ? (
												<span className={styles.metaItem}>
													<SparkleIcon weight="fill" className={styles.metaIcon} />
													{i18n._(NEW_PLAYER)}
												</span>
											) : null}
										</div>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			) : null}
			{history !== null && recent.length === 0 && !currentActivity?.name ? (
				<div className={styles.emptyMarker}>
					<Trans>No activity yet</Trans>
				</div>
			) : null}
		</div>
	);
});
